import { useEffect, useId, useRef, useState } from 'react'
import type { Mix } from './associations-model.js'
import { nombre, nuitLongue, partsMix, pourcent } from './associations-model.js'

/**
 * LES PETITES COURBES D'UNE ASSOCIATION (20/10/2026).
 *
 * Une association fait 1 à 6 soirées par an : pas de quoi brancher le
 * graphique temporel des soirées (des quarts d'heure). Ici, un point = une
 * soirée. Une seule échelle par graphique (jamais deux axes) ; un chiffre
 * inconnu est un TROU, jamais un point à 0. Chaque série a sa couleur fixe
 * (l'ordre de la sélection, jamais le rang) et une légende ; le survol d'une
 * colonne dit les valeurs exactes et la date.
 */

/** Les couleurs des séries, dans un ordre fixe — celles des graphiques des Soirées. */
export const COULEURS_SERIES = ['var(--violet)', 'var(--cyan)', 'var(--emerald)', 'var(--amber)', 'var(--rose)', 'var(--blue, #4f7cff)'] as const

export interface SerieCourbe {
  nom: string
  couleur: string
  /** Une valeur par colonne ; `null` = inconnu (un trou). */
  valeurs: Array<number | null>
}

const H = 176
const PAD_G = 40
const PAD_D = 8
const PAD_H = 10
const PAD_B = 24

/**
 * La largeur RÉELLE du graphique, en pixels : le dessin se fait à l'échelle 1,
 * pour que les étiquettes gardent leurs 13 px (plancher typographique) à
 * 390 px comme à 1920 px — un viewBox fixe les rétrécissait sur téléphone.
 */
function useLargeur(): [React.RefObject<HTMLDivElement | null>, number] {
  const ref = useRef<HTMLDivElement | null>(null)
  const [w, setW] = useState(560)
  useEffect(() => {
    const el = ref.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver((e) => { const x = Math.round(e[0]?.contentRect.width ?? 0); if (x > 0) setW(x) })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])
  return [ref, w]
}

function echelle(max: number): { haut: number; pas: number } {
  if (max <= 0) return { haut: 1, pas: 1 }
  const brut = max / 3
  const p10 = 10 ** Math.floor(Math.log10(brut))
  const pas = [1, 2, 2.5, 5, 10].map((m) => m * p10).find((x) => x >= brut) ?? 10 * p10
  return { haut: Math.ceil(max / pas) * pas, pas }
}

/**
 * Barres groupées (une couleur par série) ou lignes. `colonnes` : le libellé
 * court sous chaque colonne ; `details` : la ligne longue du survol.
 */
export function MiniGraphe(props: {
  kind: 'barres' | 'ligne'
  colonnes: string[]
  details?: string[]
  series: SerieCourbe[]
  format?: (n: number) => string
  ariaLabel: string
  /** Pourcentages : l'axe s'arrête à 100 au plus. */
  pourcentage?: boolean
}) {
  const id = useId()
  const [ref, W] = useLargeur()
  const [survol, setSurvol] = useState<number | null>(null)
  const fmt = props.format ?? ((n: number) => nombre(n))
  const n = props.colonnes.length
  const valeurs = props.series.flatMap((s) => s.valeurs).filter((v): v is number => v !== null)
  if (n === 0 || valeurs.length === 0) return <div ref={ref}><p className="lc-hint asso-graphe__vide">Pas encore de soirée chiffrée.</p></div>
  const { haut, pas } = echelle(Math.max(...valeurs))
  const plafond = props.pourcentage ? Math.min(100, haut) || 1 : haut
  const largeur = (W - PAD_G - PAD_D) / n
  const y = (v: number) => PAD_H + (H - PAD_H - PAD_B) * (1 - Math.min(v, plafond) / plafond)
  const x = (i: number) => PAD_G + largeur * i + largeur / 2
  const graduations: number[] = []
  for (let g = 0; g <= plafond + 1e-9; g += pas) graduations.push(g)
  const nbSeries = props.series.length
  const barre = Math.max(4, Math.min(26, (largeur - 6) / nbSeries - 2))
  // Une étiquette d'axe sur deux (ou trois…) quand les colonnes sont serrées : ~64 px par étiquette.
  const saut = Math.max(1, Math.ceil(n / Math.max(1, Math.floor((W - PAD_G) / 64))))

  return (
    <div className="asso-graphe" ref={ref}>
      <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} role="img" aria-labelledby={`${id}-t`} className="asso-graphe__svg"
        onMouseLeave={() => setSurvol(null)}>
        <title id={`${id}-t`}>{props.ariaLabel}</title>
        {graduations.map((g) => (
          <g key={g}>
            <line x1={PAD_G} x2={W - PAD_D} y1={y(g)} y2={y(g)} className="asso-graphe__grille" />
            <text x={PAD_G - 6} y={y(g) + 4} textAnchor="end" className="asso-graphe__axe">{props.pourcentage ? `${g}` : nombre(g)}</text>
          </g>
        ))}
        {survol !== null ? <rect x={PAD_G + largeur * survol} y={PAD_H} width={largeur} height={H - PAD_H - PAD_B} className="asso-graphe__survol" /> : null}
        {props.kind === 'barres' ? props.series.map((s, k) => s.valeurs.map((v, i) => v === null ? null : (
          <rect key={`${k}-${i}`} x={x(i) - (barre * nbSeries + 2 * (nbSeries - 1)) / 2 + k * (barre + 2)} y={y(v)}
            width={barre} height={Math.max(1, y(0) - y(v))} rx={Math.min(4, barre / 2)} fill={s.couleur} />
        ))) : props.series.map((s, k) => {
          // Un trou coupe la ligne : deux soirées connues autour d'une inconnue ne sont pas reliées.
          const morceaux: Array<Array<[number, number]>> = []
          let cur: Array<[number, number]> = []
          s.valeurs.forEach((v, i) => {
            if (v === null) { if (cur.length) morceaux.push(cur); cur = []; return }
            cur.push([x(i), y(v)])
          })
          if (cur.length) morceaux.push(cur)
          return (
            <g key={k}>
              {morceaux.map((m, j) => m.length > 1 ? (
                <polyline key={j} points={m.map((p) => p.join(',')).join(' ')} fill="none" stroke={s.couleur} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
              ) : null)}
              {s.valeurs.map((v, i) => v === null ? null : (
                <circle key={i} cx={x(i)} cy={y(v)} r={4} fill={s.couleur} className="asso-graphe__point" />
              ))}
            </g>
          )
        })}
        {props.colonnes.map((c, i) => i % saut === 0 || i === n - 1 ? (
          <text key={i} x={x(i)} y={H - 6} textAnchor="middle" className="asso-graphe__axe">{c}</text>
        ) : null)}
        {/* Les zones de survol : plus larges que les marques (toute la colonne). */}
        {props.colonnes.map((_, i) => (
          <rect key={`h-${i}`} x={PAD_G + largeur * i} y={0} width={largeur} height={H} fill="transparent"
            onMouseEnter={() => setSurvol(i)} onFocus={() => setSurvol(i)} />
        ))}
      </svg>
      {survol !== null ? (
        <div className="asso-graphe__bulle" role="status" style={{ left: `${Math.min(80, Math.max(0, ((x(survol) - 60) / W) * 100))}%` }}>
          <strong>{props.details?.[survol] ?? props.colonnes[survol]}</strong>
          {props.series.map((s) => (
            <span key={s.nom} className="asso-graphe__bulle-ligne">
              <i style={{ background: s.couleur }} aria-hidden="true" />{s.nom} : {s.valeurs[survol] === null || s.valeurs[survol] === undefined ? '—' : fmt(s.valeurs[survol]!)}
            </span>
          ))}
        </div>
      ) : null}
      {props.series.length > 1 || props.series[0]?.nom ? (
        <ul className="asso-legende" aria-label="Légende">
          {props.series.map((s) => <li key={s.nom}><i style={{ background: s.couleur }} aria-hidden="true" />{s.nom}</li>)}
        </ul>
      ) : null}
    </div>
  )
}

const FORMULES: Array<{ cle: keyof Mix; nom: string; couleur: string }> = [
  { cle: 'essentiel', nom: 'Essentiel', couleur: 'var(--cyan)' },
  { cle: 'soft', nom: 'Soft Mood', couleur: 'var(--emerald)' },
  { cle: 'prestige', nom: 'Prestige', couleur: 'var(--violet)' },
  { cle: 'autre', nom: 'Autres billets', couleur: 'var(--text-3)' },
]

/** Le mélange des formules, en une barre à 100 % et sa légende chiffrée (utile pour le stock du bar). */
export function BarreMix({ mix, titre }: { mix: Mix | null; titre?: string }) {
  const p = partsMix(mix)
  if (!mix || !p) return <span className="lc-hint">Aucune formule connue</span>
  const visibles = FORMULES.filter((f) => mix[f.cle] > 0)
  return (
    <div className="asso-mix">
      {titre ? <span className="asso-mix__titre">{titre}</span> : null}
      <div className="asso-mix__barre" role="img" aria-label={visibles.map((f) => `${f.nom} ${p[f.cle]} %`).join(', ')}>
        {visibles.map((f) => <span key={f.cle} style={{ flexGrow: mix[f.cle], background: f.couleur }} title={`${f.nom} : ${nombre(mix[f.cle])} billets (${p[f.cle]} %)`} />)}
      </div>
      <ul className="asso-legende asso-legende--mix">
        {visibles.map((f) => <li key={f.cle}><i style={{ background: f.couleur }} aria-hidden="true" />{f.nom} {pourcent(p[f.cle])} <span className="lc-hint">({nombre(mix[f.cle])})</span></li>)}
      </ul>
    </div>
  )
}

/** Le détail de survol d'une colonne « nuit ». */
export const detailNuit = (nuit: string) => `Nuit du ${nuitLongue(nuit)}`
